import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import {
  getAlgoliaClient,
  ALGOLIA_PRODUCTS_INDEX,
} from './algoliaClient';
import { formatSearchPrice } from './formatSearchPrice';
import { syncSearchPanelPosition } from './syncSearchPanelPosition';

const MIN_QUERY_LENGTH = 2;

/**
 * @param {object} options
 * @param {HTMLElement} options.container
 * @param {React.RefObject<HTMLElement|null>} [options.navRef]
 * @param {string} [options.initialQuery]
 * @param {(query: string) => void} [options.onQueryChange]
 * @param {(query: string) => void} options.onSearch
 * @param {(slug: string) => void} options.onProductSelect
 * @param {(key: string, fallback?: string) => string} options.t
 */
export function createProductAutocomplete({
  container,
  navRef,
  initialQuery = '',
  onQueryChange,
  onSearch,
  onProductSelect,
  t,
}) {
  const searchClient = getAlgoliaClient();
  let currentQuery = initialQuery;
  let layoutListenerActive = false;
  let layoutSyncRafId = null;

  const handleLayoutSync = () => {
    if (layoutSyncRafId != null) return;
    layoutSyncRafId = requestAnimationFrame(() => {
      layoutSyncRafId = null;
      syncSearchPanelPosition(container, navRef);
    });
  };

  const setLayoutListeners = (active) => {
    if (active === layoutListenerActive) return;
    layoutListenerActive = active;
    if (active) {
      window.addEventListener('resize', handleLayoutSync);
      window.addEventListener('scroll', handleLayoutSync, true);
    } else {
      window.removeEventListener('resize', handleLayoutSync);
      window.removeEventListener('scroll', handleLayoutSync, true);
    }
  };

  const instance = autocomplete({
    container,
    panelContainer: document.body,
    openOnFocus: true,
    stallThreshold: 280,
    placeholder: t('navbar.search_placeholder'),
    initialState: { query: initialQuery },
    /** Plein écran sur tablette/mobile, panneau ancré sous la navbar sur desktop */
    detachedMediaQuery: '(max-width: 1023px)',
    classNames: {
      root: 'kilim-aa-root',
      form: 'kilim-aa-form',
      input: 'kilim-aa-input',
      inputWrapper: 'kilim-aa-input-wrapper',
      inputWrapperPrefix: 'kilim-aa-input-prefix',
      inputWrapperSuffix: 'kilim-aa-input-suffix',
      label: 'kilim-aa-label',
      submitButton: 'kilim-aa-submit',
      clearButton: 'kilim-aa-clear',
      loadingIndicator: 'kilim-aa-loading',
      panel: 'kilim-aa-panel',
      panelLayout: 'kilim-aa-panel-layout',
      list: 'kilim-aa-list',
      item: 'kilim-aa-item',
      source: 'kilim-aa-source',
      sourceHeader: 'kilim-aa-source-header',
      sourceFooter: 'kilim-aa-source-footer',
      sourceNoResults: 'kilim-aa-no-results',
      detachedOverlay: 'kilim-aa-detached-overlay',
      detachedContainer: 'kilim-aa-detached-container',
      detachedFormContainer: 'kilim-aa-detached-form',
    },
    translations: {
      submitButtonTitle: t('navbar.search_placeholder'),
      clearButtonTitle: t('search.clear'),
      detachedCancelButtonText: t('search.cancel'),
    },
    onStateChange({ state }) {
      currentQuery = state.query;
      onQueryChange?.(state.query);
      setLayoutListeners(state.isOpen);
      if (state.isOpen) {
        requestAnimationFrame(handleLayoutSync);
      }
    },
    onSubmit({ state, event }) {
      event.preventDefault();
      const query = state.query.trim();
      instance.setIsOpen(false);
      onSearch(query);
    },
    getSources({ query }) {
      if (!query || query.trim().length < MIN_QUERY_LENGTH) {
        return [];
      }

      const trimmed = query.trim();

      return [
        {
          sourceId: 'products',
          getItems() {
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: ALGOLIA_PRODUCTS_INDEX,
                  params: {
                    query: trimmed,
                    hitsPerPage: 6,
                  },
                },
              ],
            });
          },
          onSelect({ item, setIsOpen }) {
            setIsOpen(false);
            if (item?.slug) {
              onProductSelect(item.slug);
            }
          },
          templates: {
            item({ item, components, html }) {
              const priceLabel = formatSearchPrice(item);
              const hasPromo =
                Number(item.promoPrice) > 0 &&
                Number(item.promoPrice) < Number(item.basePrice);

              return html`
                <div class="kilim-aa-item-content">
                  ${
                    item.imageUrl
                      ? html`<img
                          src="${item.imageUrl}"
                          alt=""
                          class="kilim-aa-item-image"
                          width="48"
                          height="48"
                          loading="lazy"
                        />`
                      : html`<span class="kilim-aa-item-image kilim-aa-item-image--empty"></span>`
                  }
                  <div class="kilim-aa-item-body">
                    <span class="kilim-aa-item-name">
                      ${components.Highlight({ hit: item, attribute: 'name' })}
                    </span>
                    <span class="kilim-aa-item-meta">
                      ${item.shopName ? html`<span class="kilim-aa-item-shop">${item.shopName}</span>` : ''}
                      ${
                        item.shopName && item.categoryName
                          ? html`<span class="kilim-aa-item-sep" aria-hidden="true">·</span>`
                          : ''
                      }
                      ${item.categoryName ? html`<span class="kilim-aa-item-category">${item.categoryName}</span>` : ''}
                    </span>
                  </div>
                  ${
                    priceLabel
                      ? html`<span class="kilim-aa-item-price ${hasPromo ? 'kilim-aa-item-price--promo' : ''}">
                          ${priceLabel}
                        </span>`
                      : ''
                  }
                </div>
              `;
            },
            noResults({ html }) {
              return html`<div class="kilim-aa-no-results">
                <p class="kilim-aa-no-results-text">
                  ${t('search.no_suggestions', { query: trimmed })}
                </p>
              </div>`;
            },
            footer({ html }) {
              return html`
                <button type="button" class="kilim-aa-view-all" data-view-all="true">
                  <span>
                    ${t('search.view_all_results', { query: trimmed })}
                  </span>
                  <svg class="kilim-aa-view-all-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L10.94 10 7.23 6.29a.75.75 0 111.06-1.06l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              `;
            },
          },
        },
      ];
    },
  });

  const handleViewAllClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.kilim-aa-view-all')) return;

    event.preventDefault();
    event.stopPropagation();
    instance.setIsOpen(false);
    onSearch(currentQuery.trim());
  };

  document.addEventListener('mousedown', handleViewAllClick, true);

  const originalDestroy = instance.destroy.bind(instance);
  instance.destroy = () => {
    if (layoutSyncRafId != null) {
      cancelAnimationFrame(layoutSyncRafId);
      layoutSyncRafId = null;
    }
    setLayoutListeners(false);
    document.removeEventListener('mousedown', handleViewAllClick, true);
    originalDestroy();
  };

  return instance;
}
